/**
 * G12: resolve an ask straight from its notification. Pure helpers shared by the service worker
 * (which chooses the buttons) and the page (which signs and sends the command; the passkey-free
 * `run.answer` / `run.approve` kinds only need this device's endorsed key).
 *
 * Buttons carry decrypted content (option labels), so they exist only when the notification text
 * itself is allowed: previews on and the ask decrypted. A locked screen with hidden previews gets
 * "Open" only.
 */
import type { CommandPayload } from '@viko/protocol'
import type { AskEvent } from './types.js'

export interface QuickAction {
  action: string
  title: string
}

/** Chrome/Android show at most two buttons; iOS PWAs show none (the tap opens the app). */
export const MAX_QUICK_ACTIONS = 2

const MARK = /\s*\((recommended|suggested)\)\s*/i
const clean = (s: string) => s.replace(MARK, '').trim()

export function quickActions(ask: AskEvent, max = MAX_QUICK_ACTIONS): QuickAction[] {
  if (ask.kind === 'approval')
    return [
      { action: 'allow', title: 'Allow' },
      { action: 'deny', title: 'Deny' },
    ].slice(0, max)
  if (ask.questions.length !== 1) return []
  const q = ask.questions[0]
  if (!q || q.multiSelect || q.secret || !q.options.length) return []
  const rows = q.options.map((o, i) => ({ i, o, rec: MARK.test(o.label) }))
  rows.sort((a, b) => Number(b.rec) - Number(a.rec) || a.i - b.i)
  return rows.slice(0, max).map(({ i, o }) => ({ action: `opt:${i}`, title: clean(o.label) }))
}

export type ActionCommand =
  | { kind: 'run.answer'; payload: CommandPayload<'run.answer'> }
  | { kind: 'run.approve'; payload: CommandPayload<'run.approve'> }

/** The command a notification button stands for, or null if the action does not fit the ask. */
export function commandFor(ask: AskEvent, action: string): ActionCommand | null {
  if (ask.kind === 'approval') {
    if (action !== 'allow' && action !== 'deny') return null
    return {
      kind: 'run.approve',
      payload: { runId: ask.runId, approvalId: ask.approvalId, decision: action },
    }
  }
  const m = /^opt:(\d+)$/.exec(action)
  const q = ask.questions[0]
  if (!m || ask.questions.length !== 1 || !q || q.multiSelect || q.secret) return null
  const opt = q.options[Number(m[1])]
  if (!opt) return null
  return {
    kind: 'run.answer',
    payload: { runId: ask.runId, questionId: ask.questionId, answers: { [q.id]: [opt.label] } },
  }
}

/** Notification button ids the service worker forwards to the page. */
export const isQuickAction = (a: string) => a === 'allow' || a === 'deny' || /^opt:\d+$/.test(a)

/**
 * Security review R3: what a tapped notification button may do. While Viko's app lock is engaged
 * the tap never signs; it opens the ask behind the unlock screen instead.
 */
export type TapPlan = 'unlock-first' | 'already-answered' | 'open' | 'sign'

export function planNotificationTap(o: {
  locked: boolean
  ask: AskEvent | undefined
  action: string
}): TapPlan {
  if (o.locked) return 'unlock-first'
  if (!o.ask) return 'already-answered'
  return commandFor(o.ask, o.action) ? 'sign' : 'open'
}
